/**
 * App-wide toast queue (#679). Plain module state so non-React code (the order lifecycle tracker, signing flows)
 * can push; <Toaster /> renders it.
 *
 * Every toast has a dedupe `key`: pushing a key that was already shown (even if since dismissed) is a no-op, so
 * replays after a WS reconnect never toast twice. Keys are remembered in a bounded window.
 */
export type ToastTone = 'success' | 'info' | 'warn' | 'error';

export interface ToastInput {
  /** Dedupe key (e.g. "<user>:<oid>:filled", "<user>:fill:<tid>"). Defaults to a unique key. */
  key?: string;
  tone: ToastTone;
  title: string;
  body?: string;
  /** Auto-dismiss after this many ms (default 6s, errors 10s). 0 = sticky. */
  ttlMs?: number;
}

export interface Toast extends Required<Pick<ToastInput, 'tone' | 'title'>> {
  id: number;
  key: string;
  body?: string;
  ttlMs: number;
}

type Listener = () => void;

export class ToastStore {
  private items: Toast[] = [];
  private listeners = new Set<Listener>();
  private seen = new Set<string>();
  private seenOrder: string[] = [];
  private nextId = 1;

  constructor(
    private readonly maxVisible = 5,
    private readonly dedupeWindow = 2_000,
  ) {}

  /** Show a toast. Returns false (and shows nothing) when its key was already shown. */
  push(t: ToastInput): boolean {
    const id = this.nextId++;
    const key = t.key ?? `toast:${id}`;
    if (this.seen.has(key)) return false;
    this.remember(key);
    const ttlMs = t.ttlMs ?? (t.tone === 'error' ? 10_000 : 6_000);
    this.items = [...this.items, { id, key, tone: t.tone, title: t.title, body: t.body, ttlMs }].slice(-this.maxVisible);
    this.notify();
    return true;
  }

  /** Has a toast with this key been shown (within the dedupe window)? */
  has(key: string): boolean {
    return this.seen.has(key);
  }

  dismiss(id: number): void {
    const next = this.items.filter((t) => t.id !== id);
    if (next.length === this.items.length) return;
    this.items = next;
    this.notify();
  }

  /** Current toasts, oldest first. Stable reference between changes (useSyncExternalStore). */
  getSnapshot = (): readonly Toast[] => this.items;

  subscribe = (l: Listener): (() => void) => {
    this.listeners.add(l);
    return () => {
      this.listeners.delete(l);
    };
  };

  /** Test helper: drop toasts and dedupe memory (listeners stay). */
  reset(): void {
    this.items = [];
    this.seen.clear();
    this.seenOrder = [];
    this.notify();
  }

  private notify(): void {
    for (const l of [...this.listeners]) l();
  }

  private remember(key: string): void {
    this.seen.add(key);
    this.seenOrder.push(key);
    if (this.seenOrder.length > this.dedupeWindow) {
      for (const k of this.seenOrder.splice(0, this.seenOrder.length - this.dedupeWindow)) this.seen.delete(k);
    }
  }
}

/** The shared app-wide toast queue. */
export const toasts = new ToastStore();
